import React, { useState } from 'react'
import { StyledTableCell, StyledTableRow } from '../../../components/EnchancedTable'
import { Clinic, Practitioner, Role, RoleForClinics, Tenant, User, UserSource } from '../../../types'
import TenantPicker from '../../../components/TenantPicker'
import SourcePicker from '../../../components/SourcePicker'
import TokenGenerator from './TokenGenerator'
import { useCredentials } from '@dentalux/security'
import LinkedPractitioner from './LinkedPractitioner'
import UserEnabled from '../../../components/EnabledSwtich'
import {
  Avatar,
  Checkbox,
  CircularProgress,
  IconButton,
  Table,
  tableCellClasses,
  TableRow,
  Button,
  Box,
  Tooltip,
} from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import LockIcon from '@mui/icons-material/Lock'
import ScheduleIcon from '@mui/icons-material/Schedule'
import RolesForClinicsDialog from './RolesForClinicsDialog'
import { canGenerateTokens } from '../../../utils/AuthUtils'
import LinkedPractitionerEdit from './LinkedPractitionerEdit'

interface Props {
  user: User
  roles: Role[]
  tenants: Tenant[]
  clinics: Clinic[]
  saveEdit: (
    userId: string,
    rolesForClinics: RoleForClinics[] | null,
    tenant: Tenant | null,
    disabled: boolean | null,
    practitioners: Practitioner[],
    lock: boolean,
    source: UserSource | null
  ) => void
  loading: boolean
  showTokenColumn: boolean
}

const findTenant = (tenants: Tenant[] | undefined, tenantName: string | null): Tenant | null => {
  if (tenants && tenantName) {
    return tenants.find((tenant) => tenant.name === tenantName) || null
  } else {
    return null
  }
}

const UserEditRow: React.FC<Props> = ({ user, roles, tenants, clinics, saveEdit, loading, showTokenColumn }) => {
  const [edit, setEdit] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [rolesForClinics, setRolesForClinics] = useState(user.roles)
  const [tenant, setTenant] = useState(findTenant(tenants, user.tenant))
  const [enabled, setEnabled] = useState(!user.disabled)
  const [lock, setLock] = useState(user.practitioners.some((pr) => pr.lockUser))
  const [practitioners, setPractitioners] = useState(user.practitioners)
  const [source, setSource] = useState(user.source)
  const { authentication } = useCredentials()

  const showImpersonation = canGenerateTokens({ user: user, authentication: authentication })

  const toggleEdit = () => {
    setEdit(!edit)
  }

  const onChangeLock = () => {
    setLock(!lock)
  }

  const onCancel = () => {
    setRolesForClinics(user.roles)
    setTenant(findTenant(tenants, user.tenant))
    setEnabled(!user.disabled)
    setSource(user.source)
    toggleEdit()
  }

  const onSave = () => {
    saveEdit(
      user.referenceId,
      rolesForClinics,
      tenant || null,
      user.disabled === enabled ? !enabled : null,
      practitioners,
      lock,
      source !== user.source ? source : null
    )
    toggleEdit()
  }

  const handleOpenDialog = () => {
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
  }

  const handleSaveRoles = (updatedRoles: RoleForClinics[]) => {
    setRolesForClinics(updatedRoles)
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return null
    return new Date(dateString).toLocaleDateString()
  }

  const renderRolesTable = (rolesData: RoleForClinics[], showIcon = false) => (
    <Table
      size="small"
      sx={{
        [`& .${tableCellClasses.root}`]: {
          borderBottom: 'none',
        },
      }}
    >
      {rolesData.map(({ role, clinics, validUntil }) => (
        <TableRow key={`role_${role.referenceId}_clinics_${clinics.map((clinic) => clinic.referenceId).join('__')}`}>
          <StyledTableCell>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <span>{role.name}</span>
                {showIcon && validUntil && (
                  <Tooltip title={`Valid until: ${formatDate(validUntil)}`} arrow>
                    <ScheduleIcon sx={{ fontSize: '1rem', color: 'warning.main' }} />
                  </Tooltip>
                )}
              </Box>
              {!showIcon && validUntil && (
                <span style={{ fontSize: '0.75rem', color: 'text.secondary', fontStyle: 'italic' }}>
                  Until: {formatDate(validUntil)}
                </span>
              )}
            </Box>
          </StyledTableCell>
          <StyledTableCell align="right">{clinics.map((clinic) => clinic.name).join(', ')}</StyledTableCell>
        </TableRow>
      ))}
    </Table>
  )

  return edit ? (
    <>
      <StyledTableRow hover role="checkbox" tabIndex={-1} key={`edit_row_${user.referenceId}`}>
        <StyledTableCell padding="checkbox" align="center">
          <IconButton size="small" onClick={onSave} color="primary">
            {loading ? <CircularProgress size={24} sx={{ mr: 1, ml: 0.4, mb: 0.7, mt: 0.7 }} /> : <CheckIcon />}
          </IconButton>
          <IconButton size="small" onClick={onCancel}>
            <CloseIcon />
          </IconButton>
        </StyledTableCell>

        {showTokenColumn && <StyledTableCell padding="checkbox" align="right"></StyledTableCell>}

        <StyledTableCell padding="checkbox" align="right">
          <Avatar alt={user.name} src={user.imgUrl as string} component="span" />
        </StyledTableCell>

        <StyledTableCell align="left">{user.name}</StyledTableCell>

        <StyledTableCell align="right">{user.email}</StyledTableCell>

        <StyledTableCell align="right">
          {tenants.length ? <TenantPicker value={tenant} options={tenants} onChange={setTenant} /> : user.tenant}
        </StyledTableCell>

        <StyledTableCell align="right">
          <SourcePicker value={source} onChange={setSource} />
        </StyledTableCell>

        <StyledTableCell align="right">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LinkedPractitionerEdit practitioners={user.practitioners} onChange={setPractitioners} />
            <Box>
              <LockIcon color="primary" />
              <Checkbox checked={lock} onChange={onChangeLock} sx={{ height: 24, width: 24 }} />
            </Box>
          </Box>
        </StyledTableCell>

        <StyledTableCell align="center">
          <UserEnabled enabled={enabled} editable={true} onChange={setEnabled} />
        </StyledTableCell>

        <StyledTableCell align="right">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {rolesForClinics.length > 0 && renderRolesTable(rolesForClinics, true)}
            <Button variant="outlined" size="small" onClick={handleOpenDialog} sx={{ mt: 1 }}>
              Edit Roles ({rolesForClinics.length})
            </Button>
          </Box>
        </StyledTableCell>
      </StyledTableRow>

      <RolesForClinicsDialog
        open={dialogOpen}
        rolesForClinics={rolesForClinics}
        roleOptions={roles}
        clinicOptions={clinics}
        onClose={handleCloseDialog}
        onSave={handleSaveRoles}
      />
    </>
  ) : (
    <StyledTableRow hover role="checkbox" tabIndex={-1} key={`view_row_${user.referenceId}`}>
      <StyledTableCell padding="checkbox" align="center">
        <IconButton size="small" onClick={toggleEdit}>
          {loading ? <CircularProgress size={24} sx={{ mr: 1, ml: 0.4, mb: 0.7, mt: 0.7 }} /> : <EditIcon />}
        </IconButton>
      </StyledTableCell>

      {showTokenColumn && (
        <StyledTableCell padding="checkbox" align="center">
          {showImpersonation && <TokenGenerator user={user} />}
        </StyledTableCell>
      )}

      <StyledTableCell padding="checkbox" align="right">
        <Avatar alt={user.name} src={user.imgUrl as string} component="span" />
      </StyledTableCell>

      <StyledTableCell align="left">{user.name}</StyledTableCell>

      <StyledTableCell align="right">{user.email}</StyledTableCell>

      <StyledTableCell align="right">{user.tenant}</StyledTableCell>

      <StyledTableCell align="right">{user.source}</StyledTableCell>

      <StyledTableCell align="right">
        <LinkedPractitioner practitioners={user.practitioners} />
      </StyledTableCell>

      <StyledTableCell align="center">
        <UserEnabled enabled={!user.disabled} editable={false} onChange={() => {}} />
      </StyledTableCell>

      <StyledTableCell align="right">{renderRolesTable(user.roles, true)}</StyledTableCell>
    </StyledTableRow>
  )
}

export default UserEditRow
